import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { GitCommit, Radar, Activity, GitBranch, TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";

/**
 * Narrative Timeline — v2.5A Epic 6.
 * Replaces the flat chronological log with a story arc:
 *   Setup → Entry → Reaction → Convergence → Current
 * Inflection points annotated with price-move deltas.
 */

const STAGE_DESC = {
  Current:     "The latest state — what's happening right now (last ~12h).",
  Convergence: "Consensus solidifies around a direction.",
  Reaction:    "Probability responds to the incoming activity.",
  Entry:       "Tracked traders establish their positions.",
  Setup:       "The market opens and early positioning takes shape.",
};

const KIND_META = {
  activity:   { icon: Activity,   color: "text-zinc-400",   label: "Activity" },
  consensus:  { icon: GitBranch,  color: "text-blue-300",   label: "Consensus" },
  signal:     { icon: Radar,      color: "text-emerald-300", label: "Signal" },
  inflection: { icon: TrendingUp, color: "text-amber-300",  label: "Inflection" },
};

export default function Timeline() {
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: ["narrative-timeline"],
    queryFn: async () =>
      (await api.get("/intel/narrative-timeline?hours=168")).data,
    staleTime: 2 * 60_000,
    // Timeline assembly is heavier than a badge/count endpoint. Avoid
    // reassembling it every 30s while a beta user is trying to read it.
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const stages = data?.stages || [];
  const freshCount = data?.fresh_event_count || 0;

  return (
    <div className="space-y-6" data-testid="timeline-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-2">
          <span>Narrative Timeline · last 7 days</span>
          {freshCount > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[9px] uppercase tracking-widest"
                  data-testid="timeline-fresh-pill">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {freshCount} fresh in last 15m
            </span>
          )}
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-100 tracking-tight">
          The story that got us here.
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          A story arc, not a flat log. Every stage is annotated. Inflection points highlight where the story turned.
        </p>
        {data && (
          <div className="mt-2 text-xs text-zinc-500 font-mono-num flex items-center gap-3">
            <span>{data.event_count} events · {(data.inflections || []).length} inflections</span>
            {dataUpdatedAt && (
              <span className="text-zinc-600" data-testid="timeline-last-updated">
                · updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500/60 align-middle" />
              </span>
            )}
          </div>
        )}
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-800 text-xs text-zinc-300 hover:bg-zinc-900 disabled:opacity-60"
          data-testid="timeline-refresh"
        >
          <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </header>

      {isLoading && <div className="text-sm text-zinc-500">Assembling arc…</div>}

      {!isLoading && stages.length === 0 && (
        <div className="border border-zinc-800 rounded-md p-6 text-center text-sm text-zinc-500">
          No events in the last 7 days.
        </div>
      )}

      <div className="space-y-6">
        {stages.map((s, si) => (
          <StageBlock key={s.stage} stage={s} index={si} last={si === stages.length - 1} />
        ))}
      </div>
    </div>
  );
}

function StageBlock({ stage, index, last }) {
  return (
    <section className="relative" data-testid={`timeline-stage-${stage.stage.toLowerCase()}`}>
      {/* Stage header */}
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono-num">
          Stage {String(index + 1).padStart(2, "0")}
        </span>
        <h2 className="font-heading text-lg font-semibold text-zinc-100">
          {stage.stage}
        </h2>
        <span className="text-[10px] text-zinc-500">
          {stage.events.length} event{stage.events.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="text-xs text-zinc-500 mb-3 max-w-lg">
        {STAGE_DESC[stage.stage] || ""}
      </p>

      {/* Events */}
      <div className="border-l border-zinc-800 ml-2 pl-6 space-y-3 relative">
        {stage.events.map((e, i) => (
          <EventRow key={`${stage.stage}-${i}`} event={e} />
        ))}
        {stage.events.length === 0 && (
          <div className="text-xs text-zinc-600">No events in this stage.</div>
        )}
      </div>
      {!last && <div className="border-l border-zinc-800/50 ml-2 h-4" />}
    </section>
  );
}

function EventRow({ event }) {
  const meta = KIND_META[event.kind] || { icon: GitCommit, color: "text-zinc-400", label: event.kind };
  const Icon = event.kind === "inflection" && event.title?.includes("down")
    ? TrendingDown
    : meta.icon;

  return (
    <div className={`relative rounded-md transition-colors ${
      event.is_fresh ? "bg-emerald-500/5 ring-1 ring-emerald-500/20 px-3 -mx-3 py-2" : ""
    }`} data-testid={event.is_fresh ? "timeline-event-fresh" : "timeline-event"}>
      <span className={`absolute -left-[31px] top-1.5 w-3 h-3 rounded-full border-2 ${
        event.kind === "signal" ? "bg-emerald-500 border-emerald-300" :
        event.kind === "consensus" ? "bg-blue-500 border-blue-300" :
        event.kind === "inflection" ? "bg-amber-500 border-amber-300" :
        "bg-zinc-700 border-zinc-500"
      } ${event.is_fresh ? "animate-pulse ring-2 ring-emerald-400/40" : ""}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={12} className={meta.color} />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              {meta.label}
            </span>
            {event.is_fresh && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300 uppercase tracking-widest font-heading">
                new
              </span>
            )}
          </div>
          <div className="text-sm text-zinc-100 mt-0.5 leading-snug">
            {event.signal_id ? (
              <Link to={`/app/signals/${event.signal_id}`} className="hover:underline break-words">
                {event.title}
              </Link>
            ) : event.market_id ? (
              <Link to={`/app/markets/${event.market_id}`} className="hover:underline break-words">
                {event.title}
              </Link>
            ) : (
              <span className="break-words">{event.title}</span>
            )}
          </div>
          {event.detail && (
            <div className="text-[11px] text-zinc-500 mt-0.5 break-words leading-snug">{event.detail}</div>
          )}
        </div>
        <div className="text-[10px] text-zinc-500 whitespace-nowrap font-mono-num">
          {timeAgo(event.at)}
        </div>
      </div>
    </div>
  );
}
